import { AppThunk } from '../../../app/store';
import { AgentFirstCartEditVia } from '../../../constants/agentFirstCartEdit';
import { CartItemAdditionTypes } from '../../../constants/event';
import { cartActions } from '../../../reducers/cartSlice';
import { dialogActions } from '../../../reducers/dialogSlice';
import { CartItem } from '../../../utils/cart';
import logger from '../../../utils/logger';
import { DRAFT_CART_SCOPE } from './cartScope';
import { draftCartActions } from './draftCart.slice';

/**
 * A line the auto-combo machinery assembled out of OTHER cart lines.
 *
 * Its `autoCombinedCartItemIds` address `cartSlice`'s id space, so they mean
 * nothing once the line is sitting in a draft that numbers from 1 independently.
 *
 * Keyed on the ITEM's own data, deliberately not on `isCartItemAutoCombined` —
 * that selector ANDs in the auto-combo feature flags, so with those off a line
 * still carrying the ids would read as ordinary and be copied with them
 * silently stripped. Whether the ids are HONOURED is a feature decision;
 * whether they EXIST is a fact about the line.
 *
 * Shared by the two places that have to agree: the per-item control's render
 * gate in `CartItemReview`, and the copy paths below. Two copies of this
 * predicate is precisely the `isAutoCombined` / `autoCombinedCartItemIds`
 * divergence already logged against this feature.
 */
export const isAutoCombinedLine = (cartItem: CartItem): boolean =>
  !!(cartItem.autoCombinedCartItemIds?.length || cartItem.isAutoCombined);

/**
 * A draft-safe copy of an order line, renumbered into the draft's id space.
 *
 * JSON round-trip rather than `structuredClone`: `CartItem` is plain data, and
 * RTK freezes state in development, so a shallow spread would leave the nested
 * modifier tree shared with `cartSlice` — a draft edit would then reach into
 * the live order.
 *
 * `addedBy` is preserved. The line really did come from the AI and saying so is
 * useful in My Cart. It also keeps `addItemToCart`'s upsell handlers off the
 * draft, which replay session-level effects that have no business firing for a
 * copy.
 *
 * The combo markers are dropped here as well as gated on above — the belt to
 * that brace.
 */
const draftCopyOf = (cartItem: CartItem, cartItemId: number): CartItem => {
  const copy: CartItem = JSON.parse(JSON.stringify(cartItem));
  copy.cartItemId = cartItemId;
  delete copy.autoCombinedCartItemIds;
  delete copy.isAutoCombined;
  return copy;
};

/**
 * Write one order line into the draft, carrying its quantity.
 *
 * The id is minted from the DRAFT's own `sequenceId`, read fresh on each call:
 * the two slices number independently and both start at 1, so reusing the
 * source id would collide the moment a second item arrived — and
 * `addItemToCart` keys on `cartItemId` and OVERWRITES, so a collision is a
 * silent loss rather than an error.
 *
 * `addItemToCart` always sets quantity 1, so anything above that is a second
 * dispatch. Same shape as `writeLineToOrder` in the other direction.
 */
const copyLineIntoDraft =
  (
    cartItem: CartItem,
    quantity: number,
    via: AgentFirstCartEditVia
  ): AppThunk<number> =>
  (dispatch, getState) => {
    const { sequenceId } = getState().draftCart;
    dispatch(
      draftCartActions.addItemToCart({
        cartItem: draftCopyOf(cartItem, sequenceId),
        // HITL-1646: which journey this was. Observability only — the reducer
        // ignores it; `agentFirstCartEditMiddleware` is the sole reader.
        via,
      })
    );
    if (quantity > 1) {
      dispatch(
        draftCartActions.updateQuantity({
          cartItemId: sequenceId.toString(),
          quantity,
        })
      );
    }
    return sequenceId;
  };

/**
 * HITL-1614 — copy one AI cart line into My Cart.
 *
 * The AI's cart is untouched by a copy, which is what makes this safe to run in
 * `Shadow`: it reads `cartSlice` and never writes it.
 *
 * Quantity is deliberately NOT carried: this path has always landed the line at
 * 1 and HITL-1644 leaves it exactly as it was. That it disagrees with the
 * cart-level copy below is real and is raised separately — it is an order
 * accuracy question, not a refactor, and it does not get decided by a helper
 * being shared.
 */
export const copyCartItemToDraft =
  (cartItem: CartItem): AppThunk =>
  (dispatch) => {
    if (!cartItem) return;

    const draftId = dispatch(copyLineIntoDraft(cartItem, 1, 'copy_item'));

    logger.debug({
      message: 'Copied an AI cart line into My Cart',
      moreInfo: `${cartItem.name} -> draft cartItemId ${draftId}`,
    });
  };

/**
 * HITL-1644 — copy the WHOLE order into My Cart in one action.
 *
 * Reads are granular, writes are wholesale. Pulling the AI's work in costs the
 * customer nothing, so it can be fine-grained; writing the live order is not,
 * so that stays one action with one meaning. This is the granular side's
 * shortcut: correcting one line of a five-item order should not cost five
 * presses in.
 *
 * REPLACES, by CLEARING the draft first — HITL-1649, and a deliberate reversal.
 *
 * HITL-1644 achieved "replace, never append" by refusing outright when the
 * draft was non-empty, which made the control a once-per-cart action: after one
 * press it disabled itself until My Cart was emptied by hand. The agent's
 * question was the right one — pressing a button and having it grey itself out
 * reads as a fault, not as a guard — and the workflow it forced was worse than
 * the risk it avoided: the AI adds a line mid-order, and picking it up meant
 * dismantling My Cart line by line.
 *
 * So each press now re-seeds My Cart from the Live order, and the cost is
 * stated plainly: it DISCARDS whatever the agent had built, with no
 * confirmation. That is the trade this ticket accepted, on the reasoning that
 * the press is deliberate, the label says which direction it runs in, and the
 * tooltip says it replaces. A confirm dialog in the seconds before readback is
 * friction exactly where it costs most.
 *
 * Nothing is cleared when there is nothing to copy. An order with no copyable
 * lines leaves the draft alone rather than emptying it into silence — a press
 * that destroyed the agent's cart and put nothing in its place would be the
 * worst reading of "replace" available.
 *
 * Auto-combined lines are skipped, exactly as the per-item control excludes
 * them. Skipped and not failed: a cart with one combo in it still copies its
 * other four lines, which is the useful answer.
 *
 * Every dispatch is synchronous inside one handler, so React commits them as a
 * single render — My Cart fills in one frame rather than line by line.
 */
export const copyOrderIntoDraftCart = (): AppThunk => (dispatch, getState) => {
  const { cartItems, cartItemsQuantity } = getState().cart;
  // No sort. `cartItemId` keys are integer-like, and JS iterates those in
  // ascending numeric order whatever the insertion order was — so My Cart reads
  // in the order the customer said it without one. A `.sort()` here looked like
  // a guarantee and was dead code: no mutation of it could fail a test.
  const sourceIds = Object.keys(cartItems ?? {});
  const copyableIds = sourceIds.filter(
    (sourceId) =>
      cartItems[sourceId] && !isAutoCombinedLine(cartItems[sourceId])
  );

  // Nothing to put in its place, so nothing is taken away. This is also what
  // the control's disabled state is keyed on (`selectHasCopyableOrderLines`);
  // the guarantee lives here so a caller that skipped the selector gets it too.
  if (!copyableIds.length) return;

  const replaced = Object.keys(getState().draftCart.cartItems ?? {}).length;
  if (replaced) {
    // The centre pane may be holding one of the lines that is about to stop
    // existing. `clearDraftCart` resets `sequenceId` too, so the copies below
    // renumber from 1 — leaving the selection in place would point the pane at
    // a DIFFERENT item rather than at nothing, which is the worse of the two.
    if (getState().dialog.selectedItemCartScope === DRAFT_CART_SCOPE) {
      dispatch(dialogActions.selectItemAndResetNavigation(undefined));
    }
    dispatch(draftCartActions.clearDraftCart());
  }

  copyableIds.forEach((sourceId) => {
    dispatch(
      copyLineIntoDraft(
        cartItems[sourceId],
        cartItemsQuantity?.[sourceId] ?? 1,
        'copy_all'
      )
    );
  });

  const skipped = sourceIds.length - copyableIds.length;
  logger.info({
    message: 'Copied the whole Live order into My Cart',
    moreInfo: `${copyableIds.length} line(s) copied${
      skipped ? `, ${skipped} auto-combined line(s) skipped` : ''
    }${replaced ? `, replacing ${replaced} line(s) already in My Cart` : ''}`,
  });
};

/**
 * HITL-1615 — write one draft line into the order.
 *
 * Sole caller is `replaceOrderWithDraftCart` since HITL-1645 removed the
 * per-line promote. Kept as its own function: the three things it gets right
 * are about writing into `cartSlice`, not about replacing, and the next writer
 * into the live order will need all three.
 *
 * Three things it has to get right:
 *
 * 1. **A fresh id from `cartSlice`'s own `sequenceId`.** The two slices number
 *    independently and both start at 1.
 * 2. **`addedBy: human`.** This is not cosmetic. `useHypothesisFrames` disarms
 *    its intercept-import on the agent's first real edit, and that check is
 *    `addedBy !== AI` (authorship) OR a baseline key gone/changed (structural).
 *    An APPENDED line leaves every baseline key intact, so if the promoted item
 *    still said `addedBy: ai` neither branch would trip and the AI's marked
 *    `handoff_flush` frame could import straight over the agent's work. It is
 *    also simply true: the agent authored this line into the order.
 * 3. **Quantity carried over.** `addItemToCart` always sets 1.
 */
const writeLineToOrder =
  (cartItem: CartItem, quantity: number): AppThunk<number> =>
  (dispatch, getState) => {
    const { sequenceId, cartItems } = getState().cart;
    // `addItemToCart` keys on `cartItem.cartItemId` and overwrites whatever is
    // there, so an id that is already taken silently DESTROYS a line of the live
    // order — in `Intercept`, that is a POS-visible loss. `sequenceId` should
    // always be past the last id, but it is maintained by a different slice than
    // the one this item came from, so the max is taken rather than trusted.
    const takenIds = Object.keys(cartItems ?? {}).map(Number);
    const cartItemId = Math.max(sequenceId, ...takenIds.map((id) => id + 1), 1);
    const promoted: CartItem = JSON.parse(JSON.stringify(cartItem));
    promoted.cartItemId = cartItemId;
    promoted.addedBy = CartItemAdditionTypes.human;

    dispatch(cartActions.addItemToCart({ cartItem: promoted }));
    if (quantity > 1) {
      dispatch(
        cartActions.updateQuantity({
          cartItemId: cartItemId.toString(),
          quantity,
        })
      );
    }
    return cartItemId;
  };

/**
 * HITL-1615 — cart-level promote: REPLACE the order with My Cart.
 *
 * Replace, not append, is what avoids the duplicate trap: an agent who shadowed
 * the whole order would otherwise double every line.
 *
 * Deliberately NOT `clearCart()`. That returns `initialCartState` and would take
 * the coupons, loyalties and POS items with it — none of which this story is
 * replacing, and none of which the agent asked to lose. Only the ITEMS are
 * replaced, one delete at a time.
 *
 * Every dispatch here is synchronous inside one handler, so React commits them
 * as a single render: the cart is never observably empty between the deletes and
 * the writes, and `OrderReview`'s cart effect sees one transition rather than
 * two.
 */
export const replaceOrderWithDraftCart =
  (): AppThunk => (dispatch, getState) => {
    const { cartItems, cartItemsQuantity } = getState().draftCart;
    const draftIds = Object.keys(cartItems ?? {});
    if (!draftIds.length) return;

    const existingIds = Object.keys(getState().cart.cartItems ?? {});
    existingIds.forEach((id) => dispatch(cartActions.deleteCartItem(id)));

    draftIds.forEach((id) => {
      dispatch(writeLineToOrder(cartItems[id], cartItemsQuantity?.[id] ?? 1));
    });

    dispatch(draftCartActions.clearDraftCart());

    logger.info({
      message: 'Replaced the order with My Cart',
      moreInfo: `${existingIds.length} line(s) out, ${draftIds.length} in`,
    });
  };

export default copyCartItemToDraft;
